// Scrolling Reviews Strip
// - Image edge-to-edge with square aspect ratio (full photo visible, no awkward crops)
// - Compact, refined typography
// - Pauses on hover / touch
// - Images loaded EAGERLY — lazy loading breaks the carousel's waitForImagesToLoad gate
//   because off-screen cards never trigger onload.

if (!customElements.get('scrolling-review-strip')) {
  class ScrollingReviewStrip extends HTMLElement {
    constructor() {
      super();
      this.track = this.querySelector('.scrolling-review-strip__track');
      this.loadingElement = this.querySelector('.scrolling-review-strip__loading');
      this.productId = this.dataset.productId || '';
      this.scrollSpeed = parseInt(this.dataset.scrollSpeed, 10) || 5;
      this.locale = this.dataset.locale || '';
      this.isPaused = false;
      this.currentOffset = 0;
      this.scrollStep = 0;
      this.loopPoint = 0;
      this.animationFrameId = null;
      this.originalReviewElements = [];

      if (!this.productId) return;
      if (!this.track) return;

      if (document.readyState === 'complete') {
        this.init();
      } else {
        window.addEventListener('load', () => this.init());
      }
    }

    async init() {
      try {
        const reviews = this.findReviewsFromProductReviewsComponent();
        if (reviews && reviews.length > 0) {
          window.loadedReviews = reviews;
          this.renderReviews();
          this.initScrolling();
        } else {
          await this.loadReviewsFromCSV();
        }
      } catch (error) {
        console.error('[scrolling-review-strip] init error', error);
        this.showEmptyState();
      }
    }

    findReviewsFromProductReviewsComponent() {
      const productReviewsElement = document.querySelector('product-reviews');
      if (productReviewsElement && productReviewsElement._initialized) {
        if (productReviewsElement.allReviews && productReviewsElement.allReviews.length > 0) {
          if (this.productId) {
            const filtered = productReviewsElement.allReviews.filter((review) => (
              (review.product_id && review.product_id.toString() === this.productId.toString()) ||
              (review.productId && review.productId.toString() === this.productId.toString())
            ));
            return filtered.length > 0 ? filtered : null;
          }
          return productReviewsElement.allReviews;
        }
      }
      return null;
    }

    async loadReviewsFromCSV() {
      if (!this.track || !this.productId) { this.showEmptyState(); return; }
      const currentLocale = (this.locale || document.documentElement.lang || 'en').toUpperCase();
      const localeKey = (this.locale || document.documentElement.lang || 'en').toLowerCase();
      const urlsElement = document.getElementById('review-csv-urls') || document.getElementById('product-reviews-urls');
      let urls = {};
      if (urlsElement) { try { urls = JSON.parse(urlsElement.textContent); } catch (e) {} }

      if (this.productId && urls[localeKey]) {
        try {
          const urlString = urls[localeKey].toString();
          if (urlString.indexOf('_' + this.productId + '.csv') > -1) {
            const response = await fetch(urls[localeKey]);
            if (response.ok) {
              this.parseReviews(await response.text());
              this.renderReviews();
              this.initScrolling();
              return;
            }
          }
        } catch (e) {}
      }

      const csvUrl = this.productId ? `/assets/${currentLocale}_${this.productId}.csv` : `/assets/${currentLocale}_default.csv`;
      try {
        const response = await fetch(csvUrl);
        if (response.ok) {
          this.parseReviews(await response.text());
          this.renderReviews();
          this.initScrolling();
          return;
        }

        if (currentLocale !== 'EN') {
          if (urls['en']) {
            try {
              const urlString = urls['en'].toString();
              if (urlString.indexOf('_' + this.productId + '.csv') > -1) {
                const r2 = await fetch(urls['en']);
                if (r2.ok) {
                  this.parseReviews(await r2.text());
                  this.renderReviews();
                  this.initScrolling();
                  return;
                }
              }
            } catch (e) {}
          }
          const fallbackUrl = this.productId ? `/assets/EN_${this.productId}.csv` : `/assets/EN_default.csv`;
          const fb = await fetch(fallbackUrl);
          if (fb.ok) {
            this.parseReviews(await fb.text());
            this.renderReviews();
            this.initScrolling();
            return;
          }
        }
        if (urls['legacy'] && this.productId) {
          try {
            const lg = await fetch(urls['legacy']);
            if (lg.ok) {
              this.parseReviews(await lg.text());
              this.renderReviews();
              this.initScrolling();
              return;
            }
          } catch (e) {}
        }
        this.showEmptyState();
      } catch (e) {
        this.showEmptyState();
      }
    }

    parseReviews(csvText) {
      const lines = csvText.split('\n');
      if (lines.length === 0) return;
      const headers = this.parseCSVLine(lines[0]);
      const reviews = [];
      for (let i = 1; i < lines.length; i++) {
        if (!lines[i].trim()) continue;
        try {
          const values = this.parseCSVLine(lines[i]);
          const review = {};
          headers.forEach((header, index) => {
            let value = values[index] || '';
            if (typeof value === 'string') value = value.trim();
            review[header] = value;
          });
          if (review.state && (review.state.toLowerCase() === 'published' || review.state.toLowerCase() === 'unpublished')) {
            reviews.push(review);
          }
        } catch (e) {}
      }
      window.loadedReviews = reviews;
    }

    parseCSVLine(line) {
      const result = [];
      let current = '';
      let inQuotes = false;
      for (let i = 0; i < line.length; i++) {
        const char = line[i];
        if (char === '"') {
          if (inQuotes && i + 1 < line.length && line[i + 1] === '"') { current += '"'; i++; }
          else inQuotes = !inQuotes;
        } else if (char === ',' && !inQuotes) {
          result.push(current.replace(/^"|"$/g, '').replace(/""/g, '"'));
          current = '';
        } else {
          current += char;
        }
      }
      result.push(current.replace(/^"|"$/g, '').replace(/""/g, '"'));
      return result;
    }

    renderReviews() {
      if (this.loadingElement) this.loadingElement.style.display = 'none';

      let reviews = [];
      if (window.loadedReviews && Array.isArray(window.loadedReviews)) {
        let filteredByProductId;
        if (this.productId) {
          filteredByProductId = window.loadedReviews.filter((review) => (
            (review.product_id && review.product_id.toString() === this.productId.toString()) ||
            (review.productId && review.productId.toString() === this.productId.toString()) ||
            (!review.product_id && !review.productId)
          ));
        } else {
          filteredByProductId = window.loadedReviews;
        }
        reviews = filteredByProductId.filter((r) => r.imageUrl && r.imageUrl.trim() !== '');
      }

      if (reviews.length === 0) { this.showEmptyState(); return; }

      const reviewElements = reviews.map((r) => this.createReviewElement(r));
      this.track.innerHTML = '';
      reviewElements.forEach((el) => this.track.appendChild(el));
      this.originalReviewElements = [...reviewElements];
    }

    escapeHtml(s) {
      if (s == null) return '';
      return String(s).replace(/[&<>"']/g, (c) => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' })[c]);
    }

    createReviewElement(review) {
      const reviewElement = document.createElement('div');
      reviewElement.classList.add('scrolling-review-strip__item');

      let imageUrl = '';
      if (review.imageUrl) imageUrl = review.imageUrl.split(',')[0].trim();

      let cleanTitle = '';
      if (review.title) {
        const parts = review.title.split('\n').filter((p) => p.trim());
        cleanTitle = parts.length > 0 ? parts[parts.length - 1].trim() : review.title;
      }

      const author = review.author || 'Anonymous';

      reviewElement.innerHTML = `
        ${imageUrl ? `<img src="${this.escapeHtml(imageUrl)}" alt="Review photo" class="scrolling-review-strip__image" loading="eager" decoding="async">` : ''}
        <div class="scrolling-review-strip__body">
          <div class="scrolling-review-strip__rating">${this.createStarRating(parseFloat(review.rating))}</div>
          ${cleanTitle ? `<h4 class="scrolling-review-strip__title">${this.escapeHtml(cleanTitle)}</h4>` : ''}
          <div class="scrolling-review-strip__author">— ${this.escapeHtml(author)}</div>
        </div>
      `;
      return reviewElement;
    }

    createStarRating(rating) {
      if (isNaN(rating)) rating = 5;
      const fullStars = Math.floor(rating);
      const hasHalfStar = (rating % 1) >= 0.5;
      const emptyStars = 5 - fullStars - (hasHalfStar ? 1 : 0);
      return `
        <div class="scrolling-review-strip__stars" role="img" aria-label="${rating} out of 5 stars">
          ${Array(fullStars).fill('<span class="scrolling-review-strip__star">★</span>').join('')}
          ${hasHalfStar ? '<span class="scrolling-review-strip__star">★</span>' : ''}
          ${Array(emptyStars).fill('<span class="scrolling-review-strip__star scrolling-review-strip__star--empty">☆</span>').join('')}
        </div>
      `;
    }

    initScrolling() {
      this.isPaused = false;
      this.currentOffset = 0;
      this.track.style.transform = 'translate3d(0px, 0, 0)';
      const container = this.querySelector('.scrolling-review-strip__container');
      if (!container || !this.track || this.originalReviewElements.length === 0) return;

      this.waitForImagesToLoad().then(() => {
        this.ensureSufficientTrackWidth(container);
        this.loopPoint = this.originalReviewElements.reduce((width, el) => {
          const style = window.getComputedStyle(el);
          const marginRight = parseFloat(style.marginRight) || 0;
          return width + el.offsetWidth + marginRight;
        }, 0);
        if (this.loopPoint <= 0) return;

        const minPxPerFrame = 0.5;
        const maxPxPerFrame = 2.0;
        this.scrollStep = minPxPerFrame + (this.scrollSpeed - 1) * ((maxPxPerFrame - minPxPerFrame) / 9);

        this.addInteractionListeners();
        this.startScrollLoop();
      });
    }

    startScrollLoop() {
      if (this.animationFrameId) cancelAnimationFrame(this.animationFrameId);
      this.scrollLoop();
    }

    scrollLoop() {
      if (this.isPaused) return;
      this.currentOffset -= this.scrollStep;
      if (this.currentOffset <= -this.loopPoint) {
        const overlap = Math.abs(this.currentOffset) - this.loopPoint;
        this.currentOffset = -overlap;
      }
      this.track.style.transform = `translate3d(${this.currentOffset}px, 0, 0)`;
      this.animationFrameId = requestAnimationFrame(this.scrollLoop.bind(this));
    }

    waitForImagesToLoad() {
      const images = this.track.querySelectorAll('img');
      if (images.length === 0) return Promise.resolve();
      // Safety: cap the total wait so a failed image never blocks scroll init forever
      const timeout = new Promise((resolve) => setTimeout(resolve, 4000));
      const allImages = Promise.all(Array.from(images).map((img) => {
        if (img.complete && img.naturalWidth > 0) return Promise.resolve();
        return new Promise((resolve) => {
          img.onload = resolve;
          img.onerror = resolve;
        });
      }));
      return Promise.race([allImages, timeout]);
    }

    ensureSufficientTrackWidth(container) {
      const containerWidth = container.offsetWidth;
      let trackWidth = this.track.scrollWidth;
      const minRequiredWidth = containerWidth * 2;
      if (this.originalReviewElements.length === 0) return;
      let cloneCount = 0;
      while (trackWidth < minRequiredWidth) {
        this.originalReviewElements.forEach((element) => {
          const clone = element.cloneNode(true);
          this.track.appendChild(clone);
          cloneCount++;
        });
        trackWidth = this.track.scrollWidth;
        if (cloneCount > 100) break;
      }
    }

    addInteractionListeners() {
      this.track.addEventListener('mouseenter', () => {
        if (!this.isPaused) { this.isPaused = true; }
      });
      this.track.addEventListener('mouseleave', () => {
        if (this.isPaused) { this.isPaused = false; this.scrollLoop(); }
      });
      this.track.addEventListener('touchstart', () => {
        if (!this.isPaused) this.isPaused = true;
      }, { passive: true });
      this.track.addEventListener('touchend', () => {
        if (this.isPaused) { this.isPaused = false; this.scrollLoop(); }
      });
      this.track.addEventListener('touchcancel', () => {
        if (this.isPaused) { this.isPaused = false; this.scrollLoop(); }
      });
    }

    disconnectedCallback() {
      if (this.animationFrameId) {
        cancelAnimationFrame(this.animationFrameId);
        this.animationFrameId = null;
      }
    }

    showEmptyState() {
      if (this.loadingElement) this.loadingElement.style.display = 'none';
      if (this.track) {
        const emptyElement = document.createElement('div');
        emptyElement.classList.add('scrolling-review-strip__empty');
        emptyElement.textContent = 'No reviews available';
        this.track.innerHTML = '';
        this.track.appendChild(emptyElement);
      }
    }
  }

  customElements.define('scrolling-review-strip', ScrollingReviewStrip);

  if (!window.loadedReviews) {
    window.loadedReviews = [];
    const originalProductReviews = customElements.get('product-reviews');
    if (originalProductReviews) {
      const originalParseReviews = originalProductReviews.prototype.parseReviews;
      originalProductReviews.prototype.parseReviews = function (csvText) {
        originalParseReviews.call(this, csvText);
        window.loadedReviews = this.allReviews;
      };
    }
  }
}
